/**
 * The "better price at …" hint of the Market screen (spec §7.3, plan §6 M2-07): the station that pays most for an
 * item right now, priced through `quote()` with the player's standing toward each station's faction.
 */
import type { M2Data, SimFactionId, StationDef, StationId } from '../data/m2Schemas'
import type { MarketState } from '../space/contracts'
import { quote, type Quote, type QuoteItem } from './quote'

export interface BestSell {
  station: StationDef
  quote: Quote
}

/**
 * The market station (other than `exclude`) with the highest sell total for `qty` of `item` at game time `t`
 * (the highest unit price when `qty` is 0), or null when no other station buys it. Ties keep data order.
 * PT-2 (D-803): a strategic quote clamps `qty` to the station's room, so the total is for what fits; a station with
 * no room for a positive `qty` is skipped.
 */
export function bestSellStation(
  data: M2Data,
  market: MarketState,
  item: QuoteItem,
  qty: number,
  t: number,
  standings: Readonly<Record<SimFactionId, number>>,
  exclude: StationId | null = null,
): BestSell | null {
  let best: BestSell | null = null
  for (const station of data.stations.stations) {
    if (station.id === exclude) continue
    const q = quote(data, market, station.id, item, qty, 'sell', t, standings[station.faction])
    if (q === null) continue
    if (qty > 0 && q.qty !== undefined && !(q.qty > 0)) continue
    if (best === null || q.total > best.quote.total || (q.total === best.quote.total && q.unit > best.quote.unit)) {
      best = { station, quote: q }
    }
  }
  return best
}
